import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Tag } from '@phosphor-icons/react';

const TEMPLATE_TYPES = [
  { value: 'loe', label: 'Letter of Engagement' },
  { value: 'quotation', label: 'Quotation' },
  { value: 'aml', label: 'AML Check Letter' },
  { value: 'engagement', label: 'Engagement Letter' },
  { value: 'other', label: 'Other' },
];

export const LetterTemplateEditor = ({
  open, onOpenChange, editingTemplate, formData, setFormData,
  tags, submitting, onSubmit, onClose,
}) => {
  const insertTag = (tag) => {
    const textarea = document.getElementById('template-content');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = formData.content.substring(0, start) + tag + formData.content.substring(end);
      setFormData({ ...formData, content: newContent });
      setTimeout(() => { textarea.focus(); textarea.setSelectionRange(start + tag.length, start + tag.length); }, 0);
    } else {
      setFormData({ ...formData, content: formData.content + tag });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => o ? onOpenChange(true) : onClose()}>
      <DialogTrigger asChild>
        <Button data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Template
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create New Template'}</DialogTitle>
          <DialogDescription>Use tags like {'{{client_name}}'} to auto-fill client information</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Template Name *</Label>
              <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Standard Letter of Engagement" required data-testid="template-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Template Type *</Label>
              <Select value={formData.template_type} onValueChange={(value) => setFormData({ ...formData, template_type: value })}>
                <SelectTrigger data-testid="template-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TEMPLATE_TYPES.map((type) => (<SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="subject">Subject Line (optional)</Label>
            <Input id="subject" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="e.g., Engagement Letter for {{company_name}}" data-testid="template-subject-input" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2"><Tag className="w-4 h-4" /> Available Tags (click to insert)</Label>
            <div className="flex flex-wrap gap-2 p-3 bg-muted rounded-md">
              {tags.map((tag) => (
                <Button key={tag.tag} type="button" variant="outline" size="sm" onClick={() => insertTag(tag.tag)} className="text-xs" title={tag.description}>{tag.tag}</Button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="template-content">Letter Content *</Label>
            <Textarea id="template-content" value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} placeholder={`Dear {{client_name}},\n\nWe are pleased to confirm our appointment as your accountants for {{company_name}}.\n\nYours sincerely,\n{{accountant_name}}`} rows={15} required className="font-mono text-sm" data-testid="template-content-input" />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="template-submit-btn">
              {submitting ? 'Saving...' : editingTemplate ? 'Update Template' : 'Create Template'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { TEMPLATE_TYPES };
